import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import activoService from '../../../services/activoService';
import { getDocumentTypeLabel } from '../../../utils/documentTypes';
import { findFamilyPath, STATUS_OPTIONS } from './ActivoForm';
import { formatDateLocaleLima } from '../../../utils/dateUtils';

const printStyles = `
  @page { size: A4; margin: 15mm 10mm; }
  @media print {
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    body { margin: 0; padding: 0; background: white; }
    .no-print { display: none !important; }
    .print-container { padding: 0; margin: 0; max-width: 100%; box-shadow: none; width: 100%; }
  }
  @media screen {
    body { background: #f3f4f6; }
    .print-container {
      max-width: 210mm; margin: 20px auto; background: white;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
    .print-actions { position: fixed; top: 20px; right: 20px; z-index: 1000; display: flex; gap: 10px; }
  }
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 11pt; color: #1f2937; line-height: 1.6; background: white;
  }
  .print-container { background: white; padding: 15mm 10mm; width: 210mm; min-height: 297mm; }
  @media print { .print-container { padding: 0; width: 100%; min-height: 100%; } }
  .header { background: #0d9488; color: white; padding: 20px 25px; margin-bottom: 20px; }
  .header h1 { font-size: 20pt; font-weight: 700; margin: 0 0 8px 0; }
  .header .code { font-family: 'Courier New', monospace; font-size: 14pt; font-weight: 600; }
  .header .subtitle { font-size: 10pt; opacity: 0.9; margin-top: 4px; }
  .cards-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 20px; }
  .card { background: white; border: 1px solid #e5e7eb; padding: 16px; }
  .card-title {
    font-size: 10pt; font-weight: 600; color: #0d9488; margin: 0 0 14px 0;
    padding-bottom: 8px; border-bottom: 2px solid #e5e7eb;
  }
  .field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px; }
  .field { margin-bottom: 12px; }
  .field-label {
    font-size: 7.5pt; font-weight: 600; color: #6b7280; margin-bottom: 4px;
    text-transform: uppercase; letter-spacing: 0.3px;
  }
  .field-value { font-size: 9.5pt; color: #1f2937; font-weight: 500; }
  .field-value.mono { font-family: 'Courier New', monospace; }
  .status-badge {
    display: inline-block; padding: 4px 10px; font-size: 8pt; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.3px;
  }
  .status-pending_start { background: #fef3c7; color: #92400e; }
  .status-active { background: #d1fae5; color: #065f46; }
  .status-maintenance { background: #ffedd5; color: #9a3412; }
  .status-inactive { background: #f3f4f6; color: #374151; }
  .status-retired { background: #fee2e2; color: #991b1b; }
  .description-card { background: white; border: 1px solid #e5e7eb; padding: 16px; margin-bottom: 20px; }
  .description-content { font-size: 9pt; color: #374151; white-space: pre-wrap; }
  .footer {
    margin-top: 20px; padding-top: 12px; border-top: 1px solid #e5e7eb;
    text-align: center; font-size: 7pt; color: #9ca3af; font-style: italic;
  }
`;

const formatDate = (value) => (value ? formatDateLocaleLima(value) || '—' : '—');

const formatDocumentSeriesNumber = (document) => {
  if (!document) return '—';
  if (document.series && document.number) return `${document.series}-${document.number}`;
  return document.series || document.number || document.code || '—';
};

const formatMoney = (value, currency = 'PEN') => {
  if (value == null || value === '') return '—';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return '—';
  return `${currency} ${num.toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getStatusLabel = (status) => STATUS_OPTIONS.find((o) => o.value === status)?.label || status;

const PrintActivo = () => {
  const { id } = useParams();
  const [activo, setActivo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await activoService.getActivo(id);
        if (response.success) setActivo(response.data);
      } catch (error) {
        console.error('Error al cargar activo:', error);
      } finally {
        setLoading(false);
      }
    };
    if (id) load();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4" />
          <p className="text-gray-600">Cargando ficha técnica…</p>
        </div>
      </div>
    );
  }

  if (!activo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-red-600 text-lg">Activo no encontrado</p>
      </div>
    );
  }

  const currency = activo.acquisition_currency || 'PEN';
  const qty = parseFloat(activo.acquisition_quantity || 0);
  const unitPrice = parseFloat(activo.acquisition_unit_price || 0);
  const subtotal = qty > 0 && unitPrice >= 0 ? qty * unitPrice : null;
  const supplierName = activo.supplier?.name || '—';
  const supplierDoc = activo.supplier?.document_number;
  const supplierDocType = activo.supplier?.document_type;

  return (
    <>
      <style>{printStyles}</style>

      <div className="print-actions no-print">
        <button
          type="button"
          onClick={() => window.print()}
          className="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium shadow-md"
        >
          Imprimir
        </button>
        <button
          type="button"
          onClick={() => window.close()}
          className="px-5 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium shadow-md"
        >
          Cerrar
        </button>
      </div>

      <div className="print-container">
        <div className="header">
          <h1>FICHA TÉCNICA DE ACTIVO</h1>
          <div className="code">{activo.code}</div>
          <div className="subtitle">{activo.name}</div>
        </div>

        <div className="cards-grid">
          <div className="card">
            <div className="card-title">Identificación</div>
            <div className="field">
              <div className="field-label">Código interno</div>
              <div className="field-value mono">{activo.code}</div>
            </div>
            <div className="field">
              <div className="field-label">Denominación</div>
              <div className="field-value">{activo.name}</div>
            </div>
            <div className="field-row">
              <div className="field">
                <div className="field-label">Estado</div>
                <div className="field-value">
                  <span className={`status-badge status-${activo.status}`}>
                    {getStatusLabel(activo.status)}
                  </span>
                </div>
              </div>
              <div className="field">
                <div className="field-label">Inicio de actividades</div>
                <div className="field-value">{formatDate(activo.activity_start_date)}</div>
              </div>
            </div>
            <div className="field">
              <div className="field-label">Vida útil</div>
              <div className="field-value">
                {activo.useful_life_years
                  ? `${activo.useful_life_years} año${activo.useful_life_years !== 1 ? 's' : ''}`
                  : '—'}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-title">Clasificación y ubicación</div>
            <div className="field">
              <div className="field-label">Clasificación</div>
              <div className="field-value">{findFamilyPath(activo.family)}</div>
            </div>
            <div className="field">
              <div className="field-label">Área de la empresa</div>
              <div className="field-value">{activo.area?.name || '—'}</div>
            </div>
          </div>
        </div>

        <div className="cards-grid">
          <div className="card">
            <div className="card-title">Características técnicas</div>
            <div className="field-row">
              <div className="field">
                <div className="field-label">Marca</div>
                <div className="field-value">{activo.brand || '—'}</div>
              </div>
              <div className="field">
                <div className="field-label">Modelo</div>
                <div className="field-value">{activo.model || '—'}</div>
              </div>
            </div>
            <div className="field">
              <div className="field-label">N° de serie / placa</div>
              <div className="field-value mono">{activo.serial_number || '—'}</div>
            </div>
          </div>

          <div className="card">
            <div className="card-title">Origen y adquisición</div>
            <div className="field">
              <div className="field-label">Documento origen</div>
              <div className="field-value">
                {activo.document ? (
                  <>
                    <span className="mono">{formatDocumentSeriesNumber(activo.document)}</span>
                    <span style={{ display: 'block', fontSize: '8pt', color: '#6b7280', marginTop: 2 }}>
                      {getDocumentTypeLabel(activo.document.document_type)}
                      {activo.document.code ? ` · ${activo.document.code}` : ''}
                    </span>
                  </>
                ) : (
                  '—'
                )}
              </div>
            </div>
            <div className="field">
              <div className="field-label">Proveedor</div>
              <div className="field-value">
                {supplierName}
                {supplierDoc ? (
                  <span style={{ display: 'block', fontSize: '8pt', color: '#6b7280' }}>
                    {supplierDocType ? `${supplierDocType}: ` : ''}{supplierDoc}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="field-row">
              <div className="field">
                <div className="field-label">Fecha adquisición</div>
                <div className="field-value">{formatDate(activo.acquisition_date || activo.document?.issue_date)}</div>
              </div>
              <div className="field">
                <div className="field-label">Cantidad</div>
                <div className="field-value">
                  {activo.acquisition_quantity != null
                    ? parseFloat(activo.acquisition_quantity).toLocaleString('es-PE', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 4,
                      })
                    : '—'}
                </div>
              </div>
            </div>
            <div className="field-row">
              <div className="field">
                <div className="field-label">Precio unitario</div>
                <div className="field-value">{formatMoney(activo.acquisition_unit_price, currency)}</div>
              </div>
              <div className="field">
                <div className="field-label">Valor total</div>
                <div className="field-value">{subtotal != null ? formatMoney(subtotal, currency) : '—'}</div>
              </div>
            </div>
          </div>
        </div>

        {activo.description ? (
          <div className="description-card">
            <div className="card-title">Descripción</div>
            <div className="description-content">{activo.description}</div>
          </div>
        ) : null}

        {activo.notes ? (
          <div className="description-card">
            <div className="card-title">Notas</div>
            <div className="description-content">{activo.notes}</div>
          </div>
        ) : null}

        <div className="footer">
          Ficha generada el{' '}
          {new Date().toLocaleString('es-PE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </div>
    </>
  );
};

export default PrintActivo;
